// Mock data for Timetable Generation and Management page

export interface GenerationConfig {
  id: string;
  name: string;
  type: 'full' | 'partial' | 'year-group' | 'subject-specific';
  scope: GenerationScope;
  parameters: GenerationParameters;
  constraints: ConstraintSet;
  preferences: PreferenceWeights;
  status: 'draft' | 'running' | 'completed' | 'failed';
  createdAt: string;
  estimatedDuration: number;
}

export interface GenerationScope {
  yearGroups?: string[];
  subjects?: string[];
  departments?: string[];
  teachers?: string[];
  timeSlots?: string[];
  priority: 'high' | 'medium' | 'low';
}

export interface GenerationParameters {
  maxIterations: number;
  optimizationLevel: 'basic' | 'standard' | 'advanced';
  allowPartialSolutions: boolean;
  conflictTolerance: number;
  timeoutMinutes: number;
  parallelProcessing: boolean;
}

export interface ConstraintSet {
  hardConstraints: string[];
  softConstraints: string[];
  preferences: string[];
}

export interface PreferenceWeights {
  teacherWorkload: number;
  roomUtilization: number;
  studentPreferences: number;
  subjectContinuity: number;
}

export interface ConflictDetection {
  id: string;
  type: 'room-clash' | 'teacher-double-booking' | 'capacity-overflow' | 'constraint-violation';
  severity: 'critical' | 'major' | 'minor' | 'warning';
  description: string;
  affectedEntities: {
    teachers?: string[];
    rooms?: string[];
    subjects?: string[];
    timeslots?: string[];
    students?: string[];
  };
  detectionTime: string;
  autoResolvable: boolean;
  resolutionOptions: ResolutionOption[];
  currentStatus: 'unresolved' | 'resolving' | 'resolved' | 'ignored';
}

export interface ResolutionOption {
  id: string;
  description: string;
  strategy: 'relocate' | 'reschedule' | 'reassign' | 'split' | 'merge';
  feasibilityScore: number;
  impactAssessment: {
    affectedLessons: number;
    preferenceViolations: number;
    cascadingChanges: number;
    timeToImplement: number;
  };
  automaticallyApplicable: boolean;
  tradeoffs: string[];
}

export interface TimetableView {
  id: string;
  name: string;
  type: 'school' | 'year-group' | 'teacher' | 'student' | 'room' | 'subject' | 'department';
  timeframe: 'daily' | 'weekly' | 'bi-weekly' | 'termly' | 'yearly';
  filterCriteria: ViewFilter;
  displayOptions: ViewDisplayOptions;
  permissions: ViewPermissions;
  lastUpdated: string;
}

export interface ViewFilter {
  yearGroups?: string[];
  subjects?: string[];
  teachers?: string[];
  rooms?: string[];
  timeSlots?: string[];
  showConflicts: boolean;
  showAvailability: boolean;
  hideEmptySlots: boolean;
}

export interface ViewDisplayOptions {
  colorScheme: 'subject' | 'teacher' | 'room' | 'year-group' | 'custom';
  layout: 'grid' | 'timeline' | 'calendar' | 'list';
  grouping: string;
  sortOrder: string;
  showDetails: boolean;
  compactView: boolean;
}

export interface ViewPermissions {
  canEdit: boolean;
  canExport: boolean;
  canShare: boolean;
  visibilityScope: 'private' | 'department' | 'school' | 'public';
  editableElements: string[];
}

export interface GenerationProgress {
  configId: string;
  status: 'initializing' | 'processing' | 'optimizing' | 'validating' | 'completing';
  currentStep: string;
  progressPercentage: number;
  startTime: string;
  estimatedCompletion: string;
  lessonsScheduled: number;
  totalLessons: number;
  conflictsDetected: number;
  conflictsResolved: number;
  logs: GenerationLog[];
}

export interface GenerationLog {
  timestamp: string;
  level: 'info' | 'warning' | 'error';
  message: string;
  details?: any;
}

export interface GenerationResult {
  configId: string;
  success: boolean;
  finalTimetable: TimetableData;
  statistics: GenerationStatistics;
  unresolvedConflicts: ConflictDetection[];
  recommendations: string[];
  alternatives: TimetableAlternative[];
}

export interface GenerationStatistics {
  totalLessons: number;
  scheduledLessons: number;
  unscheduledLessons: number;
  conflictsResolved: number;
  conflictsRemaining: number;
  optimizationScore: number;
  executionTime: number;
  iterationsCompleted: number;
}

export interface TimetableData {
  periods: TimetablePeriod[];
  summary: TimetableSummary;
}

export interface TimetablePeriod {
  id: string;
  day: string;
  period: string;
  startTime: string;
  endTime: string;
  subject: string;
  teacher: string;
  room: string;
  yearGroup: string;
  classGroup: string;
  studentCount: number;
  notes?: string;
}

export interface TimetableSummary {
  totalPeriods: number;
  scheduledPeriods: number;
  freeSlots: number;
  roomUtilization: number;
  teacherUtilization: number;
}

export interface TimetableAlternative {
  id: string;
  name: string;
  score: number;
  differences: string[];
  tradeoffs: string[];
}

export const mockData = {
  generationConfigs: [
    {
      id: 'GC001',
      name: 'Full School Timetable 2024-25',
      type: 'full' as const,
      scope: {
        yearGroups: ['Year 7', 'Year 8', 'Year 9', 'Year 10', 'Year 11'],
        priority: 'high' as const,
      },
      parameters: {
        maxIterations: 10000,
        optimizationLevel: 'advanced' as const,
        allowPartialSolutions: false,
        conflictTolerance: 0.05,
        timeoutMinutes: 120,
        parallelProcessing: true,
      },
      constraints: {
        hardConstraints: ['No teacher double-booking', 'Room capacity limits', 'Subject-room requirements'],
        softConstraints: ['Teacher preferences', 'Morning subjects preference', 'Workload balance'],
        preferences: ['Student learning optimization', 'Resource efficiency'],
      },
      preferences: {
        teacherWorkload: 0.8,
        roomUtilization: 0.6,
        studentPreferences: 0.9,
        subjectContinuity: 0.7,
      },
      status: 'completed' as const,
      createdAt: '2024-09-15T08:00:00Z',
      estimatedDuration: 90,
    },
    {
      id: 'GC002',
      name: 'Year 11 Exam Preparation Schedule',
      type: 'year-group' as const,
      scope: {
        yearGroups: ['Year 11'],
        priority: 'high' as const,
      },
      parameters: {
        maxIterations: 5000,
        optimizationLevel: 'standard' as const,
        allowPartialSolutions: true,
        conflictTolerance: 0.1,
        timeoutMinutes: 60,
        parallelProcessing: false,
      },
      constraints: {
        hardConstraints: ['No teacher double-booking', 'Exam hall availability'],
        softConstraints: ['Morning preference for core subjects', 'Study period distribution'],
        preferences: ['Exam preparation focus', 'Revision time allocation'],
      },
      preferences: {
        teacherWorkload: 0.7,
        roomUtilization: 0.5,
        studentPreferences: 1.0,
        subjectContinuity: 0.9,
      },
      status: 'running' as const,
      createdAt: '2024-09-15T09:30:00Z',
      estimatedDuration: 45,
    },
    {
      id: 'GC003',
      name: 'Science Department Schedule',
      type: 'subject-specific' as const,
      scope: {
        subjects: ['Chemistry', 'Physics', 'Biology'],
        departments: ['Science'],
        priority: 'medium' as const,
      },
      parameters: {
        maxIterations: 3000,
        optimizationLevel: 'standard' as const,
        allowPartialSolutions: true,
        conflictTolerance: 0.15,
        timeoutMinutes: 30,
        parallelProcessing: true,
      },
      constraints: {
        hardConstraints: ['Laboratory requirements', 'Equipment availability'],
        softConstraints: ['Double period preferences', 'Technician availability'],
        preferences: ['Practical session optimization'],
      },
      preferences: {
        teacherWorkload: 0.6,
        roomUtilization: 0.8,
        studentPreferences: 0.7,
        subjectContinuity: 0.8,
      },
      status: 'draft' as const,
      createdAt: '2024-09-15T10:15:00Z',
      estimatedDuration: 25,
    },
  ] as GenerationConfig[],

  currentProgress: {
    configId: 'GC002',
    status: 'optimizing' as const,
    currentStep: 'Optimizing Year 11 Mathematics schedules',
    progressPercentage: 67,
    startTime: '2024-09-15T09:30:00Z',
    estimatedCompletion: '2024-09-15T10:15:00Z',
    lessonsScheduled: 134,
    totalLessons: 200,
    conflictsDetected: 8,
    conflictsResolved: 5,
    logs: [
      {
        timestamp: '2024-09-15T09:30:00Z',
        level: 'info' as const,
        message: 'Generation process started for Year 11 configuration',
      },
      {
        timestamp: '2024-09-15T09:35:00Z',
        level: 'info' as const,
        message: 'Core subjects scheduled successfully',
      },
      {
        timestamp: '2024-09-15T09:42:00Z',
        level: 'warning' as const,
        message: 'Room capacity conflict detected for Chemistry practical',
      },
      {
        timestamp: '2024-09-15T09:45:00Z',
        level: 'info' as const,
        message: 'Chemistry conflict resolved by reassigning to Lab 2',
      },
      {
        timestamp: '2024-09-15T09:50:00Z',
        level: 'info' as const,
        message: 'Beginning optimization phase',
      },
    ],
  } as GenerationProgress,

  conflicts: [
    {
      id: 'CF001',
      type: 'room-clash' as const,
      severity: 'critical' as const,
      description: 'Mathematics and Physics both scheduled in Room M01 at Period 2 Monday',
      affectedEntities: {
        rooms: ['M01'],
        subjects: ['Mathematics', 'Physics'],
        timeslots: ['Monday Period 2'],
        teachers: ['Sarah Johnson', 'David Smith'],
      },
      detectionTime: '2024-09-15T09:42:00Z',
      autoResolvable: true,
      resolutionOptions: [
        {
          id: 'RO001',
          description: 'Move Physics to Room P01',
          strategy: 'relocate' as const,
          feasibilityScore: 0.9,
          impactAssessment: {
            affectedLessons: 1,
            preferenceViolations: 0,
            cascadingChanges: 0,
            timeToImplement: 2,
          },
          automaticallyApplicable: true,
          tradeoffs: ['Slightly further from Physics prep room'],
        },
        {
          id: 'RO002',
          description: 'Reschedule Mathematics to Period 3',
          strategy: 'reschedule' as const,
          feasibilityScore: 0.7,
          impactAssessment: {
            affectedLessons: 1,
            preferenceViolations: 1,
            cascadingChanges: 2,
            timeToImplement: 5,
          },
          automaticallyApplicable: false,
          tradeoffs: ['Moves math away from preferred morning slot', 'May affect other Year 10 schedules'],
        },
      ],
      currentStatus: 'unresolved' as const,
    },
    {
      id: 'CF002',
      type: 'teacher-double-booking' as const,
      severity: 'major' as const,
      description: 'James Wilson assigned to both Year 9 English and Year 10 Drama at the same time',
      affectedEntities: {
        teachers: ['James Wilson'],
        subjects: ['English Literature', 'Drama'],
        yearGroups: ['Year 9', 'Year 10'],
        timeslots: ['Tuesday Period 4'],
      },
      detectionTime: '2024-09-15T09:48:00Z',
      autoResolvable: false,
      resolutionOptions: [
        {
          id: 'RO003',
          description: 'Reassign Drama to another teacher',
          strategy: 'reassign' as const,
          feasibilityScore: 0.6,
          impactAssessment: {
            affectedLessons: 1,
            preferenceViolations: 2,
            cascadingChanges: 3,
            timeToImplement: 10,
          },
          automaticallyApplicable: false,
          tradeoffs: ['Different teaching style', 'Teacher may have less drama experience'],
        },
        {
          id: 'RO004',
          description: 'Move Year 9 English to different time slot',
          strategy: 'reschedule' as const,
          feasibilityScore: 0.8,
          impactAssessment: {
            affectedLessons: 1,
            preferenceViolations: 1,
            cascadingChanges: 1,
            timeToImplement: 5,
          },
          automaticallyApplicable: true,
          tradeoffs: ['Minor schedule disruption for Year 9'],
        },
      ],
      currentStatus: 'resolving' as const,
    },
    {
      id: 'CF003',
      type: 'capacity-overflow' as const,
      severity: 'minor' as const,
      description: 'Room E01 assigned 32 students but capacity is only 30',
      affectedEntities: {
        rooms: ['E01'],
        subjects: ['English Literature'],
        yearGroups: ['Year 10'],
        students: ['32 students in class 10A'],
      },
      detectionTime: '2024-09-15T09:52:00Z',
      autoResolvable: true,
      resolutionOptions: [
        {
          id: 'RO005',
          description: 'Move to larger room E02 (capacity 35)',
          strategy: 'relocate' as const,
          feasibilityScore: 0.95,
          impactAssessment: {
            affectedLessons: 1,
            preferenceViolations: 0,
            cascadingChanges: 0,
            timeToImplement: 1,
          },
          automaticallyApplicable: true,
          tradeoffs: ['None - E02 is identical equipment'],
        },
      ],
      currentStatus: 'resolved' as const,
    },
  ] as ConflictDetection[],

  timetableViews: [
    {
      id: 'TV001',
      name: 'Master School Timetable',
      type: 'school' as const,
      timeframe: 'weekly' as const,
      filterCriteria: {
        showConflicts: true,
        showAvailability: false,
        hideEmptySlots: false,
      },
      displayOptions: {
        colorScheme: 'subject' as const,
        layout: 'grid' as const,
        grouping: 'year-group',
        sortOrder: 'time',
        showDetails: true,
        compactView: false,
      },
      permissions: {
        canEdit: true,
        canExport: true,
        canShare: true,
        visibilityScope: 'school' as const,
        editableElements: ['subject', 'teacher', 'room'],
      },
      lastUpdated: '2024-09-15T10:00:00Z',
    },
    {
      id: 'TV002',
      name: 'Year 11 Detailed View',
      type: 'year-group' as const,
      timeframe: 'weekly' as const,
      filterCriteria: {
        yearGroups: ['Year 11'],
        showConflicts: true,
        showAvailability: true,
        hideEmptySlots: true,
      },
      displayOptions: {
        colorScheme: 'teacher' as const,
        layout: 'calendar' as const,
        grouping: 'class',
        sortOrder: 'subject',
        showDetails: true,
        compactView: false,
      },
      permissions: {
        canEdit: false,
        canExport: true,
        canShare: false,
        visibilityScope: 'department' as const,
        editableElements: [],
      },
      lastUpdated: '2024-09-15T09:55:00Z',
    },
    {
      id: 'TV003',
      name: 'Teacher Schedule Overview',
      type: 'teacher' as const,
      timeframe: 'weekly' as const,
      filterCriteria: {
        showConflicts: false,
        showAvailability: true,
        hideEmptySlots: true,
      },
      displayOptions: {
        colorScheme: 'year-group' as const,
        layout: 'timeline' as const,
        grouping: 'teacher',
        sortOrder: 'time',
        showDetails: false,
        compactView: true,
      },
      permissions: {
        canEdit: false,
        canExport: true,
        canShare: false,
        visibilityScope: 'private' as const,
        editableElements: [],
      },
      lastUpdated: '2024-09-15T10:05:00Z',
    },
  ] as TimetableView[],

  sampleTimetable: [
    {
      id: 'TP001',
      day: 'Monday',
      period: 'Period 1',
      startTime: '08:45',
      endTime: '09:45',
      subject: 'Mathematics',
      teacher: 'Sarah Johnson',
      room: 'M01',
      yearGroup: 'Year 10',
      classGroup: '10A',
      studentCount: 28,
    },
    {
      id: 'TP002',
      day: 'Monday',
      period: 'Period 2',
      startTime: '09:45',
      endTime: '10:45',
      subject: 'Chemistry',
      teacher: 'Emily Chen',
      room: 'SL01',
      yearGroup: 'Year 10',
      classGroup: '10A',
      studentCount: 28,
    },
    {
      id: 'TP003',
      day: 'Monday',
      period: 'Period 3',
      startTime: '11:00',
      endTime: '12:00',
      subject: 'English Literature',
      teacher: 'James Wilson',
      room: 'E01',
      yearGroup: 'Year 10',
      classGroup: '10A',
      studentCount: 28,
    },
    {
      id: 'TP004',
      day: 'Monday',
      period: 'Period 4',
      startTime: '12:00',
      endTime: '13:00',
      subject: 'History',
      teacher: 'Margaret Brown',
      room: 'H01',
      yearGroup: 'Year 10',
      classGroup: '10A',
      studentCount: 28,
    },
    {
      id: 'TP005',
      day: 'Monday',
      period: 'Period 5',
      startTime: '13:45',
      endTime: '14:45',
      subject: 'Physical Education',
      teacher: 'Robert Taylor',
      room: 'Sports Hall',
      yearGroup: 'Year 10',
      classGroup: '10A',
      studentCount: 28,
    },
  ] as TimetablePeriod[],

  generationResults: {
    configId: 'GC001',
    success: true,
    finalTimetable: {
      periods: [],
      summary: {
        totalPeriods: 250,
        scheduledPeriods: 247,
        freeSlots: 3,
        roomUtilization: 87.5,
        teacherUtilization: 91.2,
      },
    },
    statistics: {
      totalLessons: 250,
      scheduledLessons: 247,
      unscheduledLessons: 3,
      conflictsResolved: 15,
      conflictsRemaining: 2,
      optimizationScore: 8.7,
      executionTime: 87,
      iterationsCompleted: 8947,
    },
    unresolvedConflicts: [],
    recommendations: [
      'Consider adding one more science laboratory for optimal scheduling',
      'Year 11 study periods could be better distributed across the week',
      'Part-time teachers might benefit from clustered schedules',
    ],
    alternatives: [
      {
        id: 'ALT001',
        name: 'Teacher-Focused Alternative',
        score: 8.3,
        differences: ['More clustered schedules for part-time staff', 'Fewer split classes'],
        tradeoffs: ['Slightly less optimal room utilization', 'Some subjects in less preferred time slots'],
      },
      {
        id: 'ALT002',
        name: 'Student-Centered Alternative',
        score: 8.5,
        differences: ['Morning slots for core subjects', 'Better break distribution'],
        tradeoffs: ['More complex teacher scheduling', 'Higher room usage variation'],
      },
    ],
  } as GenerationResult,
};
